import styled, { keyframes } from 'styled-components';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const badgePop = keyframes`
  from { opacity: 0; transform: scale(.6); }
  to   { opacity: 1; transform: scale(1); }
`;

export const FormContainer = styled.div`
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
`;

export const FormCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--line-200);
  border-radius: var(--r-xl);
  padding: var(--s-8);
  box-shadow: var(--shadow-md);
  max-width: 480px;

  @media (max-width: 768px) {
    padding: var(--s-6);
    margin: 0 var(--s-4);
  }
`;

export const FormTitle = styled.h2`
  font-family: 'Montserrat', sans-serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--ink-900);
  margin-bottom: var(--s-6);
  text-align: left;
  position: relative;

  &::after {
    content: '';
    display: block;
    width: 40px;
    height: 3px;
    background: var(--accent-600);
    border-radius: 2px;
    margin-top: var(--s-3);
  }
`;

export const FormGroup = styled.div`
  margin-bottom: var(--s-5);

  .error {
    color: var(--error-600);
    font-size: 0.875rem;
    margin-top: var(--s-1);
    display: block;
    font-family: 'Montserrat', sans-serif;
  }
`;

export const Label = styled.label`
  display: block;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  color: var(--ink-700);
  margin-bottom: var(--s-2);
  font-size: 0.875rem;

  a {
    color: var(--brand-600);
    text-decoration: underline;

    &:hover {
      color: var(--accent-600);
    }
  }
`;

export const Input = styled.input`
  width: 100%;
  height: 52px;
  padding: 0 var(--s-4);
  border: 2px solid var(--line-200);
  border-radius: var(--r-md);
  font-size: 16px;
  color: var(--ink-900);
  transition: border-color var(--dur-base) var(--ease), box-shadow var(--dur-base);
  background: var(--surface);

  &:focus {
    border-color: var(--brand-600);
    box-shadow: var(--focus-ring);
    outline: none;
  }

  &:hover:not(:focus):not(:disabled) {
    border-color: var(--brand-400);
  }

  &::placeholder {
    color: var(--ink-300);
  }

  &:disabled {
    background: var(--gray-100);
    color: var(--ink-500);
    cursor: not-allowed;
  }
`;

export const Select = styled.select`
  width: 100%;
  height: 52px;
  padding: 0 var(--s-4);
  border: 2px solid var(--line-200);
  border-radius: var(--r-md);
  font-size: 16px;
  color: var(--ink-900);
  transition: border-color var(--dur-base) var(--ease), box-shadow var(--dur-base);
  background: var(--surface);
  cursor: pointer;

  &:focus {
    border-color: var(--brand-600);
    box-shadow: var(--focus-ring);
    outline: none;
  }

  &:hover:not(:focus):not(:disabled) {
    border-color: var(--brand-400);
  }

  &:disabled {
    background: var(--gray-100);
    color: var(--ink-500);
    cursor: not-allowed;
  }
`;

export const Button = styled.button`
  width: 100%;
  height: 52px;
  padding: 0 var(--s-8);
  background-image: var(--brand-gradient);
  color: #fff;
  border: none;
  border-radius: var(--r-md);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: background-image var(--dur-base) var(--ease),
    transform var(--dur-fast) var(--ease), box-shadow var(--dur-base);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--s-2);

  &:hover:not(:disabled) {
    background-image: var(--brand-gradient-hover);
    transform: translateY(-1px);
    box-shadow: var(--shadow-cta);
  }

  &:active:not(:disabled) {
    transform: translateY(0) scale(0.99);
  }

  &:disabled {
    background-image: none;
    background-color: var(--line-200);
    color: var(--ink-500);
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
`;

export const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid var(--white);
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

export const SuccessMessage = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--s-6);
  padding: var(--s-6) 0;

  h2 {
    font-family: 'Montserrat', sans-serif;
    font-size: 20px;
    font-weight: 600;
    color: var(--ink-900);
    margin: 0;
  }

  p {
    color: var(--ink-500);
    font-size: 16px;
    margin: 0;
  }

  .badge {
    width: 72px;
    height: 72px;
    border-radius: var(--r-full);
    background: var(--success-50);
    display: flex;
    align-items: center;
    justify-content: center;
    animation: ${badgePop} var(--dur-slow) var(--ease);

    svg {
      width: 36px;
      height: 36px;
      color: var(--success-600);
    }
  }

  .summary {
    width: 100%;
    background: var(--brand-50);
    border-radius: var(--r-md);
    padding: var(--s-4);
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: var(--s-2);
  }

  .summary .row {
    display: flex;
    justify-content: space-between;
    gap: var(--s-3);
    font-size: 13px;
  }

  .summary .k {
    color: var(--ink-500);
  }

  .summary .v {
    color: var(--ink-900);
    font-weight: 600;
    text-align: right;
  }
`;

export const ErrorMessage = styled.div`
  background: var(--error-50);
  border: 1px solid var(--error-600);
  color: var(--error-600);
  padding: var(--s-4);
  border-radius: var(--r-md);
  margin-bottom: var(--s-5);
  font: var(--fs-helper);
  display: flex;
  gap: var(--s-2);
  align-items: center;
`;

// Estilos para checkboxes personalizados
export const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s-2);

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: var(--brand-600);
    cursor: pointer;
  }
`;

// Container para os passos do formulário
export const StepContainer = styled.div`
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  padding: var(--s-6);
  background: var(--surface);
  border-radius: var(--r-xl);
  box-shadow: var(--shadow-md);
  border: 1px solid var(--line-200);

  .error {
    color: var(--error-600);
    font-size: 0.8rem;
    margin-top: var(--s-1);
    display: block;
  }

  @media (max-width: 768px) {
    padding: var(--s-4);
    margin: 0 var(--s-4);
  }
`;
